import React from "react";
import { Box, Paper, Tooltip, Typography } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import SavingsIcon from "@mui/icons-material/Savings";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SellIcon from '@mui/icons-material/Sell';
import BudgetAtRiskTile from "./BudgetAtRiskTile";

const buildCards = (deloitteOverageRate) => [
  {
    label: "Total AIC Consumed",
    key: "totalAICConsumed",
    accent: "#006eff",
    icon: <BoltIcon sx={{ color: "#006eff", fontSize: 28 }} />,
    format: (v) => v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    tooltip: "Total AI Credits (AIC) consumed across all models in this period.",
  },
  {
    label: "Gross Spend - before discounts",
    key: "totalSpend",
    accent: "#F28C00",
    icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="24" height="24" fill="#F28C00"><path d="M128 96l0-16c0-44.2 86-80 192-80S512 35.8 512 80l0 16c0 30.6-41.3 57.2-102 70.7-2.4-2.8-4.9-5.5-7.4-8-15.5-15.3-35.5-26.9-56.4-35.5-41.9-17.5-96.5-27.1-154.2-27.1-21.9 0-43.3 1.4-63.8 4.1-.2-1.3-.2-2.7-.2-4.1zM432 353l0-46.2c15.1-3.9 29.3-8.5 42.2-13.9 13.2-5.5 26.1-12.2 37.8-20.3l0 15.4c0 26.8-31.5 50.5-80 65zm0-96l0-33c0-4.5-.4-8.8-1-13 15.5-3.9 30-8.6 43.2-14.2s26.1-12.2 37.8-20.3l0 15.4c0 26.8-31.5 50.5-80 65zM0 240l0-16c0-44.2 86-80 192-80s192 35.8 192 80l0 16c0 44.2-86 80-192 80S0 284.2 0 240zm384 96c0 44.2-86 80-192 80S0 380.2 0 336l0-15.4c11.6 8.1 24.5 14.7 37.8 20.3 41.9 17.5 96.5 27.1 154.2 27.1s112.3-9.7 154.2-27.1c13.2-5.5 26.1-12.2 37.8-20.3l0 15.4zm0 80.6l0 15.4c0 44.2-86 80-192 80S0 476.2 0 432l0-15.4c11.6 8.1 24.5 14.7 37.8 20.3 41.9 17.5 96.5 27.1 154.2 27.1s112.3-9.7 154.2-27.1c13.2-5.5 26.1-12.2 37.8-20.3z" /></svg>,
    format: (v) =>
      "$" + v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    tooltip: "Total USD cost of all AIC consumed at $0.01/credit - before any enterprise pool discounts are applied.",
  },
  {
    label: "Pool Savings",
    key: "totalPoolSavings",
    accent: "#26890d",
    icon: <SavingsIcon sx={{ color: "#26890d", fontSize: 28 }} />,
    format: (v) =>
      "$" + v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    tooltip: "USD amount saved through enterprise pool credits.",
  },
  {
    label: "Overage Billed",
    key: "totalOverageBilled",
    accent: "#E53935",
    icon: <WarningAmberIcon sx={{ color: "#E53935", fontSize: 28 }} />,
    format: (v) =>
      "$" + v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    tooltip: "USD amount billed as overage (credits consumed beyond pool allocation).",
  },
  {
    label: "Net Cost after Deloitte Discount",
    key: "deloitteCost",
    accent: "rgba(134, 188, 34, 1)",
    icon: <SellIcon sx={{ color: "rgba(134, 188, 34, 1)", fontSize: 28 }} />,
    format: (v) =>
      "$" + v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    tooltip: `What Deloitte actually pays for the overage at the discounted rate of $${deloitteOverageRate}/AIC, instead of the list price of $0.01/AIC.`,
  },
];

const StatCards = ({ data, deloitteOverageRate, orgId, costCenterId, platformId, isEnterprise = false }) => {
  const cards = buildCards(deloitteOverageRate ?? 0.0064);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" },
        gap: 2,
      }}
    >
      {cards.map((card) => (
        <Tooltip
          key={card.key}
          title={card.tooltip}
          placement="top"
          arrow
          componentsProps={{
            tooltip: { sx: { fontSize: "0.8rem", lineHeight: 1.5, px: 1.5, py: 1 } },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: "10px",
              border: `1px solid ${card.accent}`,
              borderTop: `3px solid ${card.accent}`,
              padding: "20px 24px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              backgroundColor: "#fff",
              cursor: "default",
              transition: "box-shadow 0.2s",
              "&:hover": {
                boxShadow: "0 10px 24px rgba(0, 0, 0, 0.12)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {card.icon}
              <Box>
                <Typography sx={{ fontSize: "0.8rem", color: "#666", fontWeight: 500 }}>
                  {card.label}
                </Typography>
                {card.sublabel && (
                  <Typography sx={{ fontSize: "0.65rem", color: "#aaa", lineHeight: 1.2 }}>
                    {card.sublabel}
                  </Typography>
                )}
              </Box>
            </Box>
            <Typography
              sx={{ fontSize: "1.9rem", fontWeight: 700, color: "#1a1a1a", lineHeight: 1 }}
            >
              {card.format(data[card.key])}
            </Typography>
          </Paper>
        </Tooltip>
      ))}

      <BudgetAtRiskTile
        orgId={orgId}
        costCenterId={costCenterId}
        platformId={platformId}
        deloitteOverageRate={deloitteOverageRate}
        isEnterprise={isEnterprise}
      />
    </Box>
  );
};

export default StatCards;
